import Occurrence from '../models/Occurrence';
import Event from '../models/Event';
import TagOptions from '../models/TagOptions';

declare var TICKETS_CHECKOUT: string;
declare var TICKETS_CHECKOUT_V2: string;
declare var GTM_DMN_ID: string;

interface CheckoutParam {
    key: string;
    value: string;
}

class CartManager {

    purchaseId:any = undefined;
    selectedQuantities:any = {};
    sendingToCheckout:boolean = false;
    presaleCode: any = undefined;

    event: any|Event;
    occurrence: any|Occurrence;
    dmnOptions: any|TagOptions;

    constructor(dmnOptions?: TagOptions) {
        this.dmnOptions = dmnOptions;
    }

    /**
     * Returns wether an occurrence has been set
     * @return {boolean}
     */
    hasOccurrence() : boolean {
        return this.occurrence ? true : false;
    };

    /**
     * Returns wether a presale code has been set
     * @return {boolean}
     */
    hasPresaleCode() : boolean {
        return this.presaleCode ? true : false;
    };

    /**
     * Returns the selected quantity of the given ticket
     * @param {string} ticketId
     * @return {number}
     */
    getTotalQuantitySelected() : number {
        let totalSelected = 0;

        for(let key in this.selectedQuantities) {
            totalSelected += this.selectedQuantities[key];
        }

        return totalSelected;
    };

    /**
     * Returns the selected quantity of the given ticket
     * @param {string} ticketId
     * @return {number}
     */
    getTotalTicketQuantitySelected(ticketId: string)  : number {
        return this.selectedQuantities[ticketId] || 0;
    };

    /**
     * Returns the selected quantity of the given ticket's group
     * @param {string} ticketId
     * @return {number}
     */
    getTotalGroupQuantitySelected(groupId: string) : number {
        let totalSelected = 0;
        let groupTicketTypes = this.occurrence.getTicketTypeIdsInGroup(groupId);

        for(let key in this.selectedQuantities) {
            if(groupTicketTypes.indexOf(key) > -1) {
                totalSelected += this.selectedQuantities[key];
            }
        }

        return totalSelected;
    };

    /**
     * Returns the total cost of the selected items
     * @return {number}
     */
    getTotalCostOfSelected() : number {
        let totalCost = 0;

        for(let key in this.selectedQuantities) {
            // only count if selected 1 or more
            if(this.selectedQuantities[key] > 0) {
                let ticketType = this.event.getTicketType(key);

                totalCost += ticketType.price * this.selectedQuantities[key];
            }
        }
        return totalCost;
    };

    /**
     * Returns the lowest availability for a ticket type
     * @param {string} ticketId
     * @param {boolean} excludeFixedQuantities
     * @return {number}
     */
    getAvailableForTicketType(ticketId: string, excludeFixedQuantities?: boolean) : number {
        let ticketType = this.occurrence.getTicketType(ticketId);

        // if ticket type is not found, return 0
        if(!ticketType) {
            return 0;
        }

        // if ticket type is sold out, return 0
        if(ticketType.is_sold_out === true) {
            return 0;
        }

        let availableTicket = ticketType.total_ticket_quota - ticketType.total_ticket_attendees_and_reservations;
        let availableGroup = 99;
        let availableEvent = 99;
        let availablePresale = 99;

        // check for ticketGroup restrictions
        let ticketGroupId = ticketType.ticket_type_group;

        if(ticketGroupId && this.occurrence.hasGroupMaxSalesLimit(ticketGroupId)) {
            let selectedSelf = this.getTotalTicketQuantitySelected(ticketId);
            let selectedGroup = this.getTotalGroupQuantitySelected(ticketGroupId);

            availableGroup = this.occurrence.ticket_type_groups[ticketGroupId].max_ticket_sales - this.occurrence.ticket_type_groups[ticketGroupId].total_ticket_attendees_and_reservations;

            // count other selected group tickets in availability number
            availableGroup = availableGroup - selectedGroup + selectedSelf;
        }

        // check for event_max_sales_restriction
        if(this.occurrence.hasMaxSalesLimit()) {
            availableEvent = this.occurrence.event_max_ticket_sales - this.occurrence.total_ticket_attendees_and_reservations;

            let selectedSelf = this.getTotalTicketQuantitySelected(ticketId);
            let selectedTotal = this.getTotalQuantitySelected();

            // count other selected tickets in availability number
            availableEvent = availableEvent - selectedTotal + selectedSelf;
        }

        // apply presale Code limits
        if(this.presaleCode) {
            let selectedSelf = this.getTotalTicketQuantitySelected( ticketId );
            let selectedTotal = this.getTotalQuantitySelected();

            // count other selected tickets in availability number
            availablePresale = this.presaleCode.quantity_remaining - selectedTotal + selectedSelf;
        }

        // get the lowest availability
        let lowestAvailability = Math.min(availableTicket, availableGroup, availableEvent, availablePresale);
        let ticketTypeFixedQuantitiesCheck = this.event.getTicketType(ticketId);

        // when true, we dont want to check the fixed/min amount per purchase as it may not be completely sold out
        if(!excludeFixedQuantities) {
            // check that the lowest available is at least higher than the lowest fixed_ticket_quantities_per_purchase or else mark as sold out
            const fixedQuantities = ticketTypeFixedQuantitiesCheck.fixed_ticket_quantities_per_purchase || [];
            lowestAvailability = fixedQuantities.length && fixedQuantities[0] > lowestAvailability ? 0 : lowestAvailability;

            // check that the lowest available is at least higher than the min_tickets_per_purchase or else mark as sold out
            const minTicketQuantity = ticketTypeFixedQuantitiesCheck.min_tickets_per_purchase || 1;
            lowestAvailability = minTicketQuantity > lowestAvailability ? 0 : lowestAvailability;
        }


        return lowestAvailability;
    };

    /**
     * @desc prepares the users cart for the checkout
     * @return {Object} params
     */
    sendToCheckout() : {}|undefined {
        if(this.sendingToCheckout) {
            return;
        }

        this.sendingToCheckout = true;
        this.purchaseId = undefined;

        let params = {
            resetCart: true,
            items: this.getItems(),
            source: this.dmnOptions.source,
            reserve : true,
            presale_code: '',
            affiliateId: '',
            tracking_id: '',
            rep_id: '',
            checkout_version: this.dmnOptions.checkoutVersion
        };

        if (this.isCheckoutVersionTwo()) {
            params.checkout_version = '2';
        }

        if(this.dmnOptions.affiliateId) {
            params.affiliateId = this.dmnOptions.affiliateId;
        }

        // if tracking ID has been set
        if(this.dmnOptions.trackingId) {
            params.tracking_id = this.dmnOptions.trackingId;
        }

        // if repping ID has been set
        if(this.dmnOptions.reppingId) {
            params.rep_id = this.dmnOptions.reppingId;
        }

        // add presale code to request
        if(this.presaleCode) {
            params.presale_code = this.presaleCode._id;
        }

        return params;
    };

    isCheckoutVersionTwo() {
        return window.DMN && typeof window.DMN.goToCheckout === 'function'
    }

    getCheckoutParams(): CheckoutParam[] {
        if (!this.purchaseId) {
            return [];
        }

        const params: CheckoutParam[] = []

        params.push({key: 'purchase_id', value:this.purchaseId});

        // add presale code
        if (this.hasPresaleCode()) {
            params.push({key: 'presale_code', value: this.presaleCode._id});
        }

        // add source
        if (this.dmnOptions.source) {
            params.push({key: 'source', value: encodeURIComponent(this.dmnOptions.source)});
        }

        if (this.dmnOptions.source === 'designmynight') {
            params.push({key: 'gtm_account', value: GTM_DMN_ID})
        } else if (this.event.google_tag_manager_id) {
            params.push({key: 'gtm_account', value: this.event.google_tag_manager_id})
        }

        // try to add google adwords linker param if available
        try {
            if(this.dmnOptions.enableAnalyticsLinker && (<any>window).ga) {
                (<any>window).ga(function(tracker:any) {
                    var linkerParam = tracker.get('linkerParam');

                    if (linkerParam && linkerParam.length) {
                        const linkerParts = linkerParam.split('=');

                        if (linkerParts.length === 2) {
                            params.push({key: linkerParam[0], value: linkerParam[1]})
                        }
                    }
                });
            }
        }
        catch(e) {
            // fail silently
        }

        return params;
    }

    /**
     * @desc returns the checkout URL with purchase information added
     * @param {boolean} showNewCheckout - If true is passed, it will return the URL of Checkout V2. Default value is false
     * @return {string}
     */
    getCheckoutUrl(getCheckoutV2Url: boolean = false) : string|any {
        if (getCheckoutV2Url === true) {
            return this.getCheckoutV2Url();
        }

        return TICKETS_CHECKOUT + 'checkout';
    }

    /**
     * @desc returns the checkout V2 URL
     * @return {string|null} Returns the URL if this.purchaseId is set. Otherwise returns null
     */
    getCheckoutV2Url() {
        if (this.dmnOptions.source !== 'designmynight' && this.event && this.event.google_tag_manager_id) {
            let gtmAccount = this.event.google_tag_manager_id;
            if (this.purchaseId) {
                return TICKETS_CHECKOUT_V2 + `/purchases/${this.purchaseId}?gtm_account=${gtmAccount}`;
            }
        }

        return this.purchaseId ? TICKETS_CHECKOUT_V2 + `/purchases/${this.purchaseId}` : null;
    }

    /**
     * @desc turns the selected quantities into an item object
     * @param {string}
     * @return {Object}
     */
    selectedQuantitiesToItem(subTypeId: string) : Object {
        return {
            item_id: this.event._id || null,
            sub_type: subTypeId || null,
            date: this.occurrence.occurrenceMoment.format('YYYY-MM-DD') || null,
            quantity: this.selectedQuantities[subTypeId] || 0,
            source: this.dmnOptions.source || ''
        };
    };

    /**
     * @desc handles get items from the current selectedQuantities
     * @param {Array<any>}
     * @return {Array<any>}
     */
    getItems() : any[] {
        let items: any[] = [];

        for(let key in this.selectedQuantities) {
            if(this.selectedQuantities[key] > 0) {
                let newItem = this.selectedQuantitiesToItem(key);
                items.push(newItem);
            }
        }

        return items;
    };

    /**
     * @desc ensure the cart is cleared
     */
    resetCart() {
        this.purchaseId = undefined;
        this.sendingToCheckout = false;
        this.selectedQuantities = {};
    }
}

export default CartManager;
